import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Check, X, Trash2, UserPlus, Building2, Star } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Admin approves enhancers onto ANY center's roster (incl. flagships). Once an
// enhancer is approved at a flagship, they can self-add that center's services.
export default function CenterRosterPanel() {
  const qc = useQueryClient();
  const [locId, setLocId] = useState("");
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const { data: locations = [] } = useQuery({ queryKey: ["admin-locations"], queryFn: () => api.get("/admin/locations").then((r) => r.data) });
  const activeLoc = locId || locations[0]?.id || "";
  const { data, isLoading } = useQuery({
    queryKey: ["admin-loc-enhancers", activeLoc],
    queryFn: () => api.get(`/admin/locations/${activeLoc}/enhancers`).then((r) => r.data),
    enabled: !!activeLoc,
  });
  const roster = data?.roster || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-loc-enhancers", activeLoc] });

  const decide = async (entry_id, action) => {
    try { await api.post(`/admin/locations/${activeLoc}/enhancers/decide`, { entry_id, action }); toast.success(action === "approve" ? "Enhancer approved" : "Enhancer rejected"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (entry_id) => {
    if (!window.confirm("Remove this enhancer from the center roster?")) return;
    try { await api.delete(`/admin/locations/${activeLoc}/enhancers/${entry_id}`); toast.success("Removed"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addByEmail = async () => {
    if (!email.trim()) return;
    setAdding(true);
    try { await api.post(`/admin/locations/${activeLoc}/enhancers/add`, { email }); toast.success("Enhancer approved at this center"); setEmail(""); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  const pending = roster.filter((r) => r.status === "pending");
  const approved = roster.filter((r) => r.status === "approved");
  const activeLocObj = locations.find((l) => l.id === activeLoc);

  return (
    <div data-testid="admin-center-roster">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-1 flex items-center gap-2"><Building2 size={20} className="text-[#144F75]" /> Center enhancer roster</h2>
      <p className="text-sm text-[#4A5D6B] mb-5">Approve enhancers onto a center. Approving them at a <b>flagship</b> lets them add that center's official services to their profile — no further oversight needed.</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <select value={activeLoc} onChange={(e) => setLocId(e.target.value)} data-testid="roster-location-select" className="eyw-input sm:max-w-xs">
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.kind === "flagship" ? " ★ flagship" : ""}</option>)}
        </select>
        <div className="flex gap-2 flex-1">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Approve an enhancer by email…" data-testid="roster-add-email" className="eyw-input flex-1" />
          <button onClick={addByEmail} disabled={!email.trim() || adding} data-testid="roster-add-btn" className="eyw-btn !py-2 text-sm disabled:opacity-50 shrink-0">{adding ? <Loader2 className="animate-spin" size={15} /> : <UserPlus size={15} />} Approve</button>
        </div>
      </div>

      {activeLocObj?.kind === "flagship" && <p className="text-xs text-[#5C8A72] mb-4 inline-flex items-center gap-1"><Star size={12} className="fill-[#5C8A72]" /> Flagship center — approved enhancers can self-publish its services.</p>}

      {isLoading ? <Loader2 className="animate-spin text-[#144F75]" /> : (
        <>
          {pending.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#B5794E] mb-2">Pending requests ({pending.length})</h3>
              <div className="space-y-2">
                {pending.map((r) => (
                  <div key={r.id} data-testid={`roster-pending-${r.id}`} className="eyw-card p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0"><div className="text-sm font-medium text-[#1A242B] truncate">{r.enhancer_name || r.enhancer_email}</div><div className="text-xs text-[#8FA89B] truncate">{r.enhancer_email}</div></div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => decide(r.id, "approve")} data-testid={`roster-approve-${r.id}`} className="inline-flex items-center gap-1 text-xs rounded-full bg-[#5C8A72] text-white px-3 py-1.5"><Check size={13} /> Approve</button>
                      <button onClick={() => decide(r.id, "reject")} data-testid={`roster-reject-${r.id}`} className="inline-flex items-center gap-1 text-xs rounded-full border border-[#E5E0D8] text-[#4A5D6B] px-3 py-1.5"><X size={13} /> Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <h3 className="text-sm font-semibold text-[#4A5D6B] mb-2">Approved ({approved.length})</h3>
          {approved.length === 0 ? <p className="text-sm text-[#8FA89B]">No approved enhancers at this center yet.</p> : (
            <div className="grid sm:grid-cols-2 gap-3">
              {approved.map((r) => (
                <div key={r.id} data-testid={`roster-approved-${r.id}`} className="eyw-card p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0"><div className="text-sm font-medium text-[#1A242B] truncate">{r.enhancer_name || r.enhancer_email}</div><div className="text-xs text-[#8FA89B] truncate">{r.enhancer_email}</div></div>
                  <button onClick={() => remove(r.id)} data-testid={`roster-remove-${r.id}`} title="Remove" className="text-[#B9C4BD] hover:text-red-500 shrink-0"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
